import { Navigate, Link } from 'react-router-dom';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { apiFetch } from '@/api/http';
import { fmtCents, fmtTs } from './AlphaOmegaDashboardPage';

// ── Types ────────────────────────────────────────────────────────────────────

export interface MonthlyPlResponse {
  pl_id: string;
  company_id: string;
  period_start: number;
  period_end: number;
  revenue_cents: number;
  expenses_cents: number;
  profit_cents: number;
  acknowledged: boolean;
  ack_note: string | null;
  ack_at: number | null;
  created_at: string;
}

interface MonthlyPlListResponse {
  entries: MonthlyPlResponse[];
  total: number;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function plProfitColor(profit_cents: number): string {
  if (profit_cents > 0)  return '#34d399';
  if (profit_cents < 0)  return '#f87171';
  return 'var(--omega-ink)';
}

// ── Page ─────────────────────────────────────────────────────────────────────

export function AlphaOmegaMonthlyPlPage() {
  if (!isOmegaAdmin()) return <Navigate to="/" replace />;

  const [period, setPeriod] = useState('');

  const queryPath = period
    ? `/alpha-omega/monthly-pl?period=${period}`
    : '/alpha-omega/monthly-pl';

  const { data, isPending, isError } = useQuery({
    queryKey: ['alpha-omega', 'monthly-pl', period],
    queryFn: () => apiFetch<MonthlyPlListResponse>(queryPath),
    refetchInterval: 30_000,
  });

  const inputStyle: React.CSSProperties = {
    background: 'rgba(255,255,255,0.06)',
    border: '1px solid var(--omega-rim, rgba(255,255,255,0.12))',
    borderRadius: 4, color: 'var(--omega-ink)', fontFamily: 'var(--omega-mono)',
    fontSize: '8.5pt', padding: '4px 8px', outline: 'none',
  };

  return (
    <div className="omega-shell" style={{ fontFamily: 'var(--omega-sans)', color: 'var(--omega-ink)', padding: '16px 20px' }}>
      <Breadcrumbs crumbs={[
        { label: 'OMEGA',       to: '/omega' },
        { label: 'Alpha-Omega', to: '/omega/alpha-omega' },
        { label: 'Monthly CPA' },
      ]} />

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20, marginTop: 8, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: '12pt', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          Monthly P&L
        </h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginLeft: 'auto' }}>
          <label style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>Period:</label>
          <input
            type="text"
            value={period}
            onChange={e => setPeriod(e.target.value)}
            placeholder="YYYY-MM"
            style={inputStyle}
          />
        </div>
      </div>

      {isPending && <div style={{ color: 'var(--omega-ink-subtle)', fontSize: '9pt' }}>Loading…</div>}
      {isError   && <div style={{ color: '#f87171', fontSize: '9pt' }}>Error loading data.</div>}

      {data && (
        <>
          <div style={{ fontSize: '8pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)', marginBottom: 12 }}>
            {data.total} Records
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 800 }}>
            {data.entries.map(e => (
              <Link
                key={e.pl_id}
                to={`/omega/alpha-omega/monthly-pl/${e.pl_id}`}
                style={{
                  display: 'grid', gridTemplateColumns: '1fr 100px 100px 100px 70px',
                  gap: 10, padding: '10px 14px',
                  background: 'rgba(255,255,255,0.02)',
                  border: '1px solid var(--omega-rim, rgba(255,255,255,0.08))',
                  borderRadius: 4, textDecoration: 'none', color: 'var(--omega-ink)',
                }}
              >
                <span style={{ fontSize: '8.5pt', color: 'var(--omega-ink-subtle)', fontFamily: 'var(--omega-mono)' }}>
                  {fmtTs(e.period_start)} – {fmtTs(e.period_end)}
                </span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: '#34d399' }}>{fmtCents(e.revenue_cents)}</span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: '#f87171' }}>({fmtCents(e.expenses_cents)})</span>
                <span style={{ fontSize: '8.5pt', fontFamily: 'var(--omega-mono)', color: plProfitColor(e.profit_cents), fontWeight: 700 }}>{fmtCents(e.profit_cents)}</span>
                <span style={{
                  fontSize: '7.5pt', fontFamily: 'var(--omega-mono)', textTransform: 'uppercase',
                  color: e.acknowledged ? '#34d399' : '#fbbf24',
                }}>
                  {e.acknowledged ? 'ack' : 'pending'}
                </span>
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
